// WalletDashboard.jsx - Updated component with API integration
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Pagination,
  Container,
  AppBar,
  Toolbar,
  InputBase,
  alpha,
  styled,
  CircularProgress,
  Alert,
  Snackbar,
} from "@mui/material";

import { load } from "@cashfreepayments/cashfree-js";
import { CASHFREE_MODE } from "../../config/environment";
import { useAuth } from "../../hooks/AuthProvider";

import {
  Search as SearchIcon,
  Add as AddIcon,
  Close as CloseIcon,
} from "@mui/icons-material";

import { GET_WALLET_DETAILS } from "../../config/urls";
import { GET_WALLET_STATS } from "../../config/urls";
import { GET_WALLET_SUMMARY } from "../../config/urls";
import { GET_WALLET_MONTHLY_SPEND } from "../../config/urls";

import { GET_WALLET_TRANSACTIONS } from "../../config/urls";

import { GET_WALLET_ADD_FUNDS } from "../../config/urls";
import { GET_WALLET_INITIATE_CASHFREE_ENDPOINT } from "../../config/urls";

const cashfree = await load({
  mode: CASHFREE_MODE,
});

// const getAuthToken = () => {
//   return localStorage.getItem('token');
// };

const SearchContainer = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginLeft: 0,
  width: "100%",
  [theme.breakpoints.up("sm")]: {
    marginLeft: theme.spacing(1),
    width: "auto",
  },
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create("width"),
    width: "100%",
    [theme.breakpoints.up("sm")]: {
      width: "12ch",
      "&:focus": {
        width: "20ch",
      },
    },
  },
}));

export default function MyWalletTable() {
  // State management
  const [walletData, setWalletData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [monthlySpend, setMonthlySpend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [showAddFundsModal, setShowAddFundsModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [amount, setAmount] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [dataCount, setDataCount] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [submitting, setSubmitting] = useState(false);

  const { getValidToken } = useAuth();

  // Fetch wallet data on component mount
  useEffect(() => {
    fetchWalletData();
    fetchTransactions(1);
    fetchMonthlySpend(); // ✅ now fetching monthly spend on mount
  }, []);

  const apiRequest = async (url, options = {}) => {
    const token = await getValidToken();
    const response = await fetch(url, {
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
        "X-Tenant-ID": localStorage.getItem("tenant"),
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  };

  const walletAPI = {
    getWalletDetails: () => apiRequest(GET_WALLET_DETAILS),

    getWalletStats: () => apiRequest(GET_WALLET_STATS),

    getTransactions: (page = 1, pageSize = 10) =>
      apiRequest(
        `${GET_WALLET_TRANSACTIONS}?page=${page}&page_size=${pageSize}`
      ),

    getMonthlySpend: () => apiRequest(GET_WALLET_MONTHLY_SPEND),

    addFunds: (amount, paymentMethod = "BANK_TRANSFER") =>
      apiRequest(GET_WALLET_ADD_FUNDS, {
        method: "POST",
        body: JSON.stringify({
          amount: amount,
          payment_method: paymentMethod,
        }),
      }),
  };

  const fetchWalletData = async () => {
    try {
      setLoading(true);
      const data = await walletAPI.getWalletStats();
      console.log("Wallet data fetched:", data);
      setWalletData(data);
    } catch (error) {
      console.error("Error fetching wallet data:", error);
      showSnackbar("Error loading wallet data", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async (page = 1) => {
    try {
      setTransactionsLoading(true);
      const response = await walletAPI.getTransactions(page, 10);
      setTransactions(response.results || []);
      setDataCount(response.count || 0);
      setTotalPages(Math.ceil(response.count / 10));
      setCurrentPage(page);
    } catch (error) {
      console.error("Error fetching transactions:", error);
      showSnackbar("Error loading transactions", "error");
    } finally {
      setTransactionsLoading(false);
    }
  };

//   const fetchMonthlySpend = async () => {
//   try {
//     const token = await getValidToken();
//     const response = await walletAPI.getMonthlySpend();

//     if (!response.ok) {
//       throw new Error(`HTTP error! status: ${response.status}`);
//     }

//     const data = await response.json();
//     console.log("Monthly spend fetched:", data);
//     setMonthlySpend(data?.current_month_spend || 0);
//   } catch (error) {
//     console.error("Error fetching monthly spend:", error);
//     showSnackbar("Error loading monthly spend", "error");
//   }
// };

  const fetchMonthlySpend = async () => {
  try {
    const data = await walletAPI.getMonthlySpend();
    console.log("Monthly spend fetched:", data);
    setMonthlySpend(data?.current_month_spend || 0);
  } catch (error) {
    console.error("Error fetching monthly spend:", error);
    showSnackbar("Error loading monthly spend", "error");
  }
};

  const handlePageChange = (event, page) => {
    fetchTransactions(page);
  };

  const handleAddFunds = () => {
    setShowAddFundsModal(true);
  };

  const handleAmountSubmit = () => {
    if (amount && parseFloat(amount) > 0) {
      setShowAddFundsModal(false);
      setShowConfirmModal(true);
    }
  };

  let cashfree = null;

  const cashfreeRef = useRef(null);
  useEffect(() => {
    const initializeSDK = async () => {
      if (typeof window === "undefined") return;

      try {
        const cf = await load({ mode: CASHFREE_MODE });
        if (!cf) {
          console.error("❌ SDK load() returned null");
          return;
        }
        console.log("✅ SDK loaded", cf);
        cashfreeRef.current = cf; // ✅ Store it in the ref
      } catch (e) {
        console.error("❌ Failed to load SDK:", e);
      }
    };

    initializeSDK();
  }, []);

  // ____________________________________________________________________________________________________________
  const handleConfirm = async () => {
    try {
      setSubmitting(true);
      const tenant = localStorage.getItem("tenant");
      const token = await getValidToken();

      const res = await fetch(`${GET_WALLET_INITIATE_CASHFREE_ENDPOINT}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
          "X-Tenant-ID": tenant,
        },
        body: JSON.stringify({ amount: parseInt(amount, 10) }),
      });

      const data = await res.json();
      console.log("init", cashfree);
      console.log("Initiate Cashfree response:", data);
      console.log("Cashfree ref:", cashfreeRef.current);
      if (data.payment_session_id && cashfreeRef.current) {
        try {
          cashfreeRef.current.checkout({
            paymentSessionId: data.payment_session_id,
            redirectTarget: "_self",
          });
        } catch (err) {
          console.error("Cashfree checkout error:", err);
          showSnackbar("Failed to open payment window", "error");
        }
      } else {
        showSnackbar(data.error || data.amount || "Failed to initiate payment.", "error");
      }
    } catch (error) {
      console.error("Error initiating payment:", error);
      showSnackbar("Something went wrong. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };
  // _________________________________________________________________________________________________________________________

  const handleCancel = () => {
    setShowAddFundsModal(false);
    setShowConfirmModal(false);
    setAmount("");
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "completed":
        return { bgcolor: "#e8f5e8", color: "#2e7d32" };
      case "pending":
        return { bgcolor: "#fff3e0", color: "#f57c00" };
      case "failed":
        return { bgcolor: "#ffebee", color: "#d32f2f" };
      default:
        return { bgcolor: "#f5f5f5", color: "#666" };
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, minHeight: "100vh" }}>
      <h2 className="heading-title">
        My Wallet
      </h2>
      
      <SearchContainer>
        <SearchIconWrapper>
          <SearchIcon sx={{ color: "gray" }} />
        </SearchIconWrapper>
        <StyledInputBase
          placeholder="Search…"
          inputProps={{ "aria-label": "search" }}
          sx={{ color: "black" }}
        />
      </SearchContainer>

      <Container maxWidth="lg" sx={{ mt: 3, mb: 3 }}>
        {/* Wallet Balance Section */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography variant="h5" component="h2">
                My Wallet Balance
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddFunds}
                sx={{ bgcolor: "#1976d2" }}
              >
                Add Funds
              </Button>
            </Box>

            <Typography
              variant="h3"
              component="div"
              sx={{ fontWeight: "bold", mb: 4 }}
            >
              {walletData ? formatCurrency(walletData.balance) : "₹0.00"}
            </Typography>

            {/* Stats Cards */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card sx={{ bgcolor: "#f8f9fa" }}>
                  <CardContent>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Last Deposit
                    </Typography>
                    <Typography
                      variant="h5"
                      component="div"
                      sx={{ fontWeight: 600 }}
                    >
                      {walletData
                        ? formatCurrency(walletData.last_deposit_amount)
                        : "₹0.00"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {walletData?.last_deposit_date
                        ? formatDate(walletData.last_deposit_date)
                        : "No deposits yet"}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card sx={{ bgcolor: "#f8f9fa" }}>
                  <CardContent>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Last Month Spend
                    </Typography>
                    <Typography
                      variant="h5"
                      component="div"
                      sx={{ fontWeight: 600 }}
                    >
                      {walletData
                        ? formatCurrency(walletData.monthly_spend)
                        : "₹0.00"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {new Date().toLocaleDateString("en-IN", {
                        month: "long",
                        year: "numeric",
                      })}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card sx={{ bgcolor: "#f8f9fa" }}>
                  <CardContent>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Current Month Spend
                    </Typography>
                    <Typography
                      variant="h5"
                      component="div"
                      sx={{ fontWeight: 600 }}
                    >
                      {monthlySpend !== null
                        ? formatCurrency(monthlySpend)
                        : "₹0.00"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Excluding tax
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Deposit History Section */}
        <Card>
          <CardContent>
            <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
              Deposit History
            </Typography>

            {transactionsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f8f9fa" }}>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          textTransform: "uppercase",
                          fontSize: "0.75rem",
                        }}
                      >
                        Type
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          textTransform: "uppercase",
                          fontSize: "0.75rem",
                        }}
                      >
                        Method
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          textTransform: "uppercase",
                          fontSize: "0.75rem",
                        }}
                      >
                        Amount
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          textTransform: "uppercase",
                          fontSize: "0.75rem",
                        }}
                      >
                        Date
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          textTransform: "uppercase",
                          fontSize: "0.75rem",
                        }}
                      >
                        Status
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transactions.length > 0 ? (
                      transactions.map((transaction) => (
                        <TableRow key={transaction.id}>
                          <TableCell sx={{ fontWeight: 500 }}>
                            {transaction.type_display}
                          </TableCell>
                          <TableCell sx={{ color: "#1976d2" }}>
                            {transaction.method_display}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, color: "#2e7d32" }}>
                            {formatCurrency(transaction.amount)}
                          </TableCell>
                          <TableCell sx={{ color: "text.secondary" }}>
                            {formatDate(transaction.created_at)}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={transaction.status_display}
                              size="small"
                              sx={{
                                ...getStatusColor(transaction.status),
                                fontWeight: 600,
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          sx={{ textAlign: "center", py: 3 }}
                        >
                          <Typography color="text.secondary">
                            No transactions found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Pagination */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-end",
                mt: 2,
                pt: 2,
                borderTop: 1,
                borderColor: "divider",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Showing{" "}
                {transactions.length > 0 ? (currentPage - 1) * 10 + 1 : 0} to{" "}
                {(currentPage - 1) * 10 + transactions.length} of{" "}
                {dataCount} results
              </Typography>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="small"
                disabled={transactionsLoading}
              />
            </Box>
          </CardContent>
        </Card>
      </Container>

      {/* Add Funds Modal */}
      <Dialog
        open={showAddFundsModal}
        onClose={handleCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Add Funds
          <IconButton onClick={handleCancel} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Amount to Add"
            type="number"
            fullWidth
            variant="outlined"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">₹</InputAdornment>
              ),
            }}
            sx={{ mt: 2 }}
            helperText="Enter the amount you want to add to your wallet"
          />
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCancel} variant="outlined" 
          >
            Cancel
          </Button>
          <Button
            onClick={handleAmountSubmit}
            variant="contained"
            disabled={!amount || parseFloat(amount) <= 0}
            sx={{ ml: 2 }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Modal */}
      <Dialog
        open={showConfirmModal}
        onClose={handleCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Confirm Transaction
          <IconButton onClick={handleCancel} size="small" disabled={submitting}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Are you sure you want to add the following amount to your wallet?
          </Typography>
          <Card sx={{ bgcolor: "#f8f9fa" }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Amount to Add
              </Typography>
              <Typography
                variant="h4"
                component="div"
                sx={{ fontWeight: "bold" }}
              >
                {amount && formatCurrency(parseFloat(amount))}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Payment Method: Bank Transfer
              </Typography>
            </CardContent>
          </Card>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={handleCancel}
            variant="outlined"
            fullWidth
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            fullWidth
            disabled={submitting}
            sx={{
              ml: 2,
              bgcolor: "#2e7d32",
              "&:hover": { bgcolor: "#1b5e20" },
            }}
          >
            {submitting ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Processing...
              </>
            ) : (
              "Confirm & Add Funds"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
